import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { Loader, Currency, Field } from "@/components/Atoms";
import {
  Shield, Users, Crown, Coins, Settings, Wrench, Ban, ScrollText, Map as MapIcon,
  UserCog, Layers, Gift, PackageOpen, Server, Lock, KeyRound, Flame, Calendar,
  Flag, Sparkles, RefreshCw
} from "lucide-react";



export function Giveaways() {
  const toast = useToast();
  const [form, setForm] = useState({
    duration_min: 60,
    channel_id: "1390314351436500992",
    winners: 1,
    role_id: "",
  });
  const [bundle, setBundle] = useState([]);
  const [picker, setPicker] = useState("currency");
  const [pickerCatalog, setPickerCatalog] = useState([]);
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerAmount, setPickerAmount] = useState(1);
  const [pickerLevel, setPickerLevel] = useState(1);
  // Full Pal Builder spec — only used when adding a pal prize with
  // the "Advanced" toggle on. Mirrors the fields the site's Pal Builder
  // ships to /api/pal/deliver so giveaway winners get the exact same
  // build the admin previews here.
  const [advancedPal, setAdvancedPal] = useState(false);
  const [palSpec, setPalSpec] = useState({
    level: 50,
    ivHp: 100, ivAtk: 100, ivDef: 100,
    soulHp: 0, soulAtk: 0, soulDef: 0, soulCraft: 0,
    trust: 0, gender: "Male",
    is_lucky: false, is_boss: false,
    passives: [], active_skills: [],
  });
  const [passiveLib, setPassiveLib] = useState({});   // {tier: [{name,id,tier}]}
  const [skillLib, setSkillLib] = useState([]);        // [{name,asset,...}]
  const [active, setActive] = useState([]);
  const [requests, setRequests] = useState([]);
  const [channels, setChannels] = useState([]);

  useEffect(() => {
    // Pull the live channel list so the admin can pick by name instead
    // of memorising 18-digit IDs. Cached server-side for 5 min; we just
    // fetch once on tab mount.
    api.get("/admin/discord-channels").then(({ data }) => {
      setChannels(data.items || []);
    }).catch(() => setChannels([]));
    // Passive + active skill libraries for the Advanced pal builder.
    api.get("/pal/passives").then(({ data }) => setPassiveLib(data || {})).catch(() => {});
    api.get("/skills/builder/active").then(({ data }) => setSkillLib(data?.skills || [])).catch(() => {});
  }, []);

  const load = () => {
    api.get("/admin/giveaways/list").then(({ data }) => {
      setActive(data.active || []);
      setRequests(data.requests || []);
    }).catch(() => {});
  };

  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, []);

  useEffect(() => {
    if (picker === "currency" || picker === "custom") { setPickerCatalog([]); return; }
    api.get(`/admin/give/catalog?kind=${picker}`).then(({ data }) => setPickerCatalog(data.results || [])).catch(() => setPickerCatalog([]));
  }, [picker]);

  const addPrize = (entry) => setBundle((b) => [...b, entry]);
  const removePrize = (i) => setBundle((b) => b.filter((_, j) => j !== i));

  const addCurrency = () => {
    const amt = parseInt(pickerAmount, 10) || 0;
    if (amt <= 0) return toast.error("Amount must be > 0");
    addPrize({ type: "currency", display: `${amt.toLocaleString()} Chillet Coins`, amount: amt });
    setPickerAmount(1);
  };
  const addCustom = (text) => addPrize({ type: "custom", display: text });

  // Search filter — no client-side truncation. With 600+ pals & 1900+
  // items, the previous `.slice(0, 60)` made anything past row 60
  // unreachable even by exact-name search if it matched too many rows.
  // Virtual-scroll handles the volume cheaply.
  const filteredPicker = pickerCatalog.filter((c) =>
    !pickerSearch || c.name?.toLowerCase().includes(pickerSearch.toLowerCase())
  );

  const start = async (e) => {
    e.preventDefault();
    if (bundle.length === 0) return toast.error("Add at least one prize.");
    if (!form.channel_id) return toast.error("Discord channel ID required.");
    try {
      const { data } = await api.post("/admin/giveaways/start", {
        ...form,
        winners: parseInt(form.winners, 10) || 1,
        duration_min: parseInt(form.duration_min, 10) || 60,
        role_id: form.role_id || null,
        prize_bundle: bundle,
      });
      toast.success(`Queued — request #${data.request_id}. Bot posts in ~20s.`);
      setBundle([]);
      setTimeout(load, 2000);
    } catch (e) { toast.error(apiError(e)); }
  };

  const endNow = async (mid) => {
    if (!window.confirm(`End giveaway ${mid} early?`)) return;
    try { await api.post(`/admin/giveaways/end/${mid}`); toast.success("Queued end"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const reroll = async (mid) => {
    if (!window.confirm(`Reroll winners for ${mid}?`)) return;
    try { await api.post(`/admin/giveaways/reroll/${mid}`); toast.success("Queued reroll"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4 fade-up" data-testid="giveaways-tab">
      <div className="glass-card p-4">
        <h3 className="text-lg mb-1" style={{ fontFamily: "'Cinzel Decorative', serif" }}>Giveaways</h3>
        <p className="text-xs text-[var(--muted)]">
          The website queues the request and the Discord bot posts the actual giveaway (with the Enter button + auto winner-pick + RCON prize delivery). Bot polls every 20 s.
        </p>
      </div>

      <form onSubmit={start} className="glass-card p-5 space-y-3" data-testid="giveaway-form">
        <h3 className="label">Create New Giveaway</h3>
        <div className="grid sm:grid-cols-4 gap-3">
          <Field label="Duration (min)"><input className="input" type="number" min="1" required value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} data-testid="giveaway-duration" /></Field>
          <Field label="Channel">
            {channels.length > 0 ? (
              <select
                className="input text-xs"
                required
                value={form.channel_id}
                onChange={(e) => setForm({ ...form, channel_id: e.target.value })}
                data-testid="giveaway-channel-id"
              >
                <option value="">— pick a channel —</option>
                {channels.map((c) => (
                  <option key={c.id} value={c.id}>
                    #{c.name}{c.parent ? ` · ${c.parent}` : ""}
                  </option>
                ))}
              </select>
            ) : (
              <input
                className="input font-mono text-xs"
                required
                placeholder="139…"
                value={form.channel_id}
                onChange={(e) => setForm({ ...form, channel_id: e.target.value })}
                data-testid="giveaway-channel-id"
              />
            )}
          </Field>
          <Field label="Winners"><input className="input" type="number" min="1" required value={form.winners} onChange={(e) => setForm({ ...form, winners: e.target.value })} data-testid="giveaway-winners" /></Field>
          <Field label="Role ID (optional)"><input className="input font-mono text-xs" placeholder="leave blank = @everyone" value={form.role_id} onChange={(e) => setForm({ ...form, role_id: e.target.value })} data-testid="giveaway-role-id" /></Field>
        </div>

        {/* Prize picker */}
        <div className="glass-card p-3 bg-black/30 space-y-2">
          <div className="flex gap-1 flex-wrap">
            {["currency", "item", "pal", "egg", "custom"].map((k) => (
              <button key={k} type="button" onClick={() => setPicker(k)} className={`btn btn-sm ${picker === k ? "" : "btn-secondary"}`} data-testid={`prize-picker-${k}`}>
                {k}
              </button>
            ))}
          </div>
          {picker === "currency" && (
            <div className="flex gap-2 items-end">
              <Field label="Skull amount"><input className="input" type="number" min="1" value={pickerAmount} onChange={(e) => setPickerAmount(e.target.value)} data-testid="prize-currency-amount" /></Field>
              <button type="button" onClick={addCurrency} className="btn" data-testid="prize-add-currency">+ Skulls</button>
            </div>
          )}
          {picker === "custom" && (
            <div className="flex gap-2 items-end">
              <Field label="Custom prize text"><input className="input" placeholder="e.g. VIP status x1 month" id="custom-prize-input" data-testid="prize-custom-input" /></Field>
              <button type="button" onClick={() => { const v = document.getElementById("custom-prize-input").value; if (v) addCustom(v); }} className="btn" data-testid="prize-add-custom">+ Custom</button>
            </div>
          )}
          {(picker === "item" || picker === "pal" || picker === "egg") && (
            <>
              <input className="input" placeholder={`Search ${picker}s…`} value={pickerSearch} onChange={(e) => setPickerSearch(e.target.value)} data-testid="prize-search" />
              <div className="text-[10px] text-[var(--muted)] -mt-1">
                Showing {filteredPicker.length.toLocaleString()} of {pickerCatalog.length.toLocaleString()}
                {pickerCatalog.length > 60 && !pickerSearch && (
                  <span className="ml-2 text-[var(--bone)]">· scroll to see all</span>
                )}
              </div>
              {picker === "item" && (
                <Field label="Amount per winner"><input className="input" type="number" min="1" value={pickerAmount} onChange={(e) => setPickerAmount(e.target.value)} /></Field>
              )}
              {picker === "pal" && (
                <>
                  <div className="flex items-center justify-between gap-3">
                    <Field label="Pal level">
                      <input
                        className="input"
                        type="number"
                        min="1"
                        max={advancedPal ? 255 : 55}
                        value={pickerLevel}
                        onChange={(e) => setPickerLevel(e.target.value)}
                        data-testid="prize-pal-level"
                      />
                    </Field>
                    <label className="flex items-center gap-2 text-xs text-[var(--muted)] cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={advancedPal}
                        onChange={(e) => setAdvancedPal(e.target.checked)}
                        data-testid="prize-pal-advanced-toggle"
                      />
                      <span>Advanced: IVs / Passives / Skills / Trust</span>
                    </label>
                  </div>
                  {advancedPal && (
                    <AdvancedPalPanel
                      spec={palSpec}
                      setSpec={setPalSpec}
                      passiveLib={passiveLib}
                      skillLib={skillLib}
                    />
                  )}
                </>
              )}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-1 max-h-60 overflow-y-auto">
                {filteredPicker.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => {
                      if (picker === "item") addPrize({ type: "item", display: `${c.name} ×${pickerAmount}`, id: c.id, amount: parseInt(pickerAmount, 10) || 1, image_url: c.image });
                      else if (picker === "pal") {
                        const lv = parseInt(advancedPal ? palSpec.level : pickerLevel, 10) || 1;
                        const spec = advancedPal ? {
                          level: lv,
                          talents: [palSpec.ivHp, palSpec.ivAtk, palSpec.ivDef],
                          rank_hp: palSpec.soulHp,
                          rank_attack: palSpec.soulAtk,
                          rank_defence: palSpec.soulDef,
                          rank_craft_speed: palSpec.soulCraft,
                          trust: palSpec.trust,
                          gender: palSpec.gender,
                          is_lucky: palSpec.is_lucky,
                          is_boss: palSpec.is_boss,
                          passives: palSpec.passives.map((p) => p.name),
                          active_skills: palSpec.active_skills.map((s) => s.name),
                        } : null;
                        const tags = [];
                        if (advancedPal) {
                          if (palSpec.is_boss) tags.push("Alpha");
                          if (palSpec.is_lucky) tags.push("Lucky");
                          if (palSpec.passives.length) tags.push(`${palSpec.passives.length}p`);
                          if (palSpec.active_skills.length) tags.push(`${palSpec.active_skills.length}sk`);
                        }
                        const displayTags = tags.length ? ` [${tags.join("/")}]` : "";
                        addPrize({
                          type: "pal",
                          display: `${c.name} (Lvl ${lv})${displayTags}`,
                          id: c.id,
                          level: lv,
                          image_url: c.image,
                          ...(spec ? { pal_spec: spec } : {}),
                        });
                      }
                      else addPrize({ type: "egg", display: c.name, id: c.id, image_url: c.image });
                    }}
                    className="glass-card p-2 text-left text-xs hover:bg-white/5 flex items-center gap-2"
                    data-testid={`prize-pick-${c.id}`}
                  >
                    {c.image ? (
                      <img
                        src={c.image}
                        alt=""
                        className="w-8 h-8 rounded object-contain flex-shrink-0 bg-black/40"
                        loading="lazy"
                        onError={(e) => {
                          // Broken image — show the "?" silhouette so the
                          // tile still reads as selectable. Prevents the
                          // browser's default broken-image icon.
                          e.currentTarget.style.display = "none";
                          e.currentTarget.nextElementSibling.style.display = "flex";
                        }}
                      />
                    ) : null}
                    <div
                      className="w-8 h-8 rounded bg-white/5 flex-shrink-0 items-center justify-center text-[var(--muted)] text-xs"
                      style={{ display: c.image ? "none" : "flex" }}
                      aria-hidden="true"
                    >
                      ?
                    </div>
                    <span className="truncate flex-1">{c.name}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {bundle.length > 0 && (
          <div>
            <h4 className="label">Prize bundle ({bundle.length})</h4>
            <ul className="space-y-1" data-testid="prize-bundle-list">
              {bundle.map((e, i) => (
                <li key={i} className="flex items-center justify-between gap-2 text-sm bg-black/30 rounded p-2">
                  <span className="flex items-center gap-2 truncate flex-1">
                    {e.image_url ? (
                      <img src={e.image_url} alt="" className="w-7 h-7 rounded object-contain flex-shrink-0 bg-black/40" loading="lazy" />
                    ) : null}
                    <span className="badge text-[var(--muted)] border-white/10">{e.type}</span>
                    <span className="truncate">{e.display}</span>
                  </span>
                  <button type="button" onClick={() => removePrize(i)} className="btn btn-sm btn-danger" data-testid={`prize-remove-${i}`}>×</button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <button className="btn" data-testid="giveaway-submit" disabled={bundle.length === 0}>Queue Giveaway</button>
      </form>

      {/* Active */}
      <div className="glass-card p-5" data-testid="active-giveaways">
        <h3 className="label mb-3">Active &amp; Recent (from bot)</h3>
        {active.length === 0 && <p className="text-sm text-[var(--muted)]">None yet.</p>}
        <ul className="space-y-2">
          {active.map((g) => {
            const channelName =
              channels.find((c) => c.id === String(g.channel_id))?.name ||
              g.channel_id;
            return (
            <li key={g.message_id} className="glass-card p-3 bg-black/20" data-testid={`active-giveaway-${g.message_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="text-sm font-medium">{g.prize}</div>
                  <div className="text-xs text-[var(--muted)] mt-1">
                    Entries: {g.entries} · Winners: {g.winners} · Ends: {g.end_time}
                    {g.ended && <span className="badge text-[var(--muted)] border-white/10 ml-1">ended</span>}
                  </div>
                  <div className="text-[10px] text-[var(--muted)] mt-1 flex items-center gap-2">
                    <span>#{channelName}</span>
                    <span className="font-mono">msg: {g.message_id}</span>
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  {!g.ended && <button onClick={() => endNow(g.message_id)} className="btn btn-sm btn-danger" data-testid={`end-${g.message_id}`}>End now</button>}
                  {g.ended && <button onClick={() => reroll(g.message_id)} className="btn btn-sm btn-secondary" data-testid={`reroll-${g.message_id}`}>Reroll</button>}
                </div>
              </div>
            </li>
          );})}
        </ul>
      </div>

      {/* Bridge requests */}
      {requests.length > 0 && (
        <div className="glass-card p-5" data-testid="giveaway-requests">
          <h3 className="label mb-3">Recent Bridge Requests</h3>
          <ul className="space-y-1 text-xs">
            {requests.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-2 bg-black/20 rounded p-2 font-mono">
                <span>#{r.id} {r.action} → <span className={r.status === "done" ? "text-[var(--teal)]" : r.status === "failed" ? "text-[var(--danger)]" : "text-[var(--muted)]"}>{r.status}</span></span>
                <span className="text-[var(--muted)] truncate max-w-[40%]">{r.error || r.message_id || r.created_at}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Compact Pal Builder — used inside the Giveaways prize picker so
 * admins can attach the full Pal Builder spec (IVs, passives, active
 * skills, trust, souls, gender, alpha/lucky flags) to a pal prize.
 *
 * The winner picks the target server at entry time (mirrors the Discord
 * bot's server-select flow). Backend clamps the extended range
 * (Lv > 65, IV > 100, PalSouls > 20) at delivery time based on that
 * server's `modded` / `vip_free` flags — so admins can safely set
 * Lv 255 here and it'll auto-downgrade for a winner who picks the
 * vanilla server.
 */
function AdvancedPalPanel({ spec, setSpec, passiveLib, skillLib }) {
  const passives = spec.passives || [];
  const actives = spec.active_skills || [];

  const addPassive = (p) => {
    if (passives.length >= 4) return;
    if (passives.some((x) => x.name === p.name)) return;
    setSpec({ ...spec, passives: [...passives, p] });
  };
  const removePassive = (i) =>
    setSpec({ ...spec, passives: passives.filter((_, j) => j !== i) });

  const addActive = (s) => {
    if (actives.length >= 3) return;
    if (actives.some((x) => x.name === s.name)) return;
    setSpec({ ...spec, active_skills: [...actives, s] });
  };
  const removeActive = (i) =>
    setSpec({ ...spec, active_skills: actives.filter((_, j) => j !== i) });

  // Passive library ships as {tier: [...]}. Flatten & sort by tier desc
  // so Legendary rolls show first, matching the Pal Builder's picker.
  const passiveOptions = Object.entries(passiveLib || {})
    .flatMap(([tier, list]) => (list || []).map((p) => ({ ...p, tier: parseInt(tier, 10) || 0 })))
    .sort((a, b) => (b.tier - a.tier) || a.name.localeCompare(b.name));

  return (
    <div className="glass-card p-3 bg-black/40 space-y-3 border border-white/5" data-testid="advanced-pal-panel">
      <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--teal)]">
        Advanced Pal Builder — same options as the site&apos;s Pal Builder
      </p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Field label={`Level (${spec.level})`}>
          <input type="range" min="1" max="255" value={spec.level}
                 onChange={(e) => setSpec({ ...spec, level: parseInt(e.target.value, 10) || 1 })}
                 className="w-full" data-testid="adv-level" />
        </Field>
        <Field label={`Trust (${spec.trust})`}>
          <input type="range" min="0" max="10" value={spec.trust}
                 onChange={(e) => setSpec({ ...spec, trust: parseInt(e.target.value, 10) || 0 })}
                 className="w-full" data-testid="adv-trust" />
        </Field>
        <Field label="Gender">
          <select className="input text-xs" value={spec.gender}
                  onChange={(e) => setSpec({ ...spec, gender: e.target.value })}
                  data-testid="adv-gender">
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="None">None</option>
          </select>
        </Field>
        <div className="flex flex-col gap-1 text-xs pt-4">
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={spec.is_lucky}
                   onChange={(e) => setSpec({ ...spec, is_lucky: e.target.checked })}
                   data-testid="adv-lucky" />
            <span>✨ Lucky</span>
          </label>
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={spec.is_boss}
                   onChange={(e) => setSpec({ ...spec, is_boss: e.target.checked })}
                   data-testid="adv-boss" />
            <span>👑 Alpha</span>
          </label>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {[
          ["HP",  "ivHp"],
          ["ATK", "ivAtk"],
          ["DEF", "ivDef"],
        ].map(([label, key]) => (
          <Field key={key} label={`IV ${label} (${spec[key]})`}>
            <input type="range" min="0" max="255" value={spec[key]}
                   onChange={(e) => setSpec({ ...spec, [key]: parseInt(e.target.value, 10) || 0 })}
                   className="w-full" data-testid={`adv-iv-${key}`} />
          </Field>
        ))}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          ["Soul HP",  "soulHp"],
          ["Soul ATK", "soulAtk"],
          ["Soul DEF", "soulDef"],
          ["Soul CFT", "soulCraft"],
        ].map(([label, key]) => (
          <Field key={key} label={`${label} (${spec[key]})`}>
            <input type="range" min="0" max="1020" step="20" value={spec[key]}
                   onChange={(e) => setSpec({ ...spec, [key]: parseInt(e.target.value, 10) || 0 })}
                   className="w-full" data-testid={`adv-soul-${key}`} />
          </Field>
        ))}
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] uppercase tracking-wider text-[var(--muted)]">
            Passives ({passives.length}/4)
          </span>
          {passives.length > 0 && (
            <button type="button" onClick={() => setSpec({ ...spec, passives: [] })}
                    className="text-[10px] text-[var(--danger)]">clear</button>
          )}
        </div>
        {passives.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-1.5">
            {passives.map((p, i) => (
              <button key={i} type="button" onClick={() => removePassive(i)}
                      className={`badge cursor-pointer passive-tier-${p.tier} !text-[10px]`}
                      data-testid={`adv-passive-${i}`}>
                {p.name} ×
              </button>
            ))}
          </div>
        )}
        <select className="input text-xs" value=""
                onChange={(e) => {
                  const p = passiveOptions.find((x) => x.name === e.target.value);
                  if (p) addPassive(p);
                }}
                disabled={passives.length >= 4}
                data-testid="adv-passive-picker">
          <option value="">— add passive —</option>
          {passiveOptions.map((p, i) => (
            <option key={`${p.tier}-${p.name}-${i}`} value={p.name}>[T{p.tier}] {p.name}</option>
          ))}
        </select>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] uppercase tracking-wider text-[var(--muted)]">
            Active Skills ({actives.length}/3)
          </span>
          {actives.length > 0 && (
            <button type="button" onClick={() => setSpec({ ...spec, active_skills: [] })}
                    className="text-[10px] text-[var(--danger)]">clear</button>
          )}
        </div>
        {actives.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-1.5">
            {actives.map((s, i) => (
              <button key={i} type="button" onClick={() => removeActive(i)}
                      className="badge cursor-pointer !text-[10px] border-[var(--teal-soft)]"
                      data-testid={`adv-active-${i}`}>
                {s.name} ×
              </button>
            ))}
          </div>
        )}
        <select className="input text-xs" value=""
                onChange={(e) => {
                  const s = skillLib.find((x) => x.name === e.target.value);
                  if (s) addActive(s);
                }}
                disabled={actives.length >= 3}
                data-testid="adv-active-picker">
          <option value="">— add active skill —</option>
          {skillLib.map((s, i) => (
            <option key={`${s.name || s.asset}-${i}`} value={s.name}>
              {s.name}{s.element ? ` · ${s.element}` : ""}
            </option>
          ))}
        </select>
      </div>

      <p className="text-[10px] text-[var(--muted)] leading-relaxed">
        Caps are applied at delivery time based on the winner&apos;s chosen
        server — modded servers get the full range, vanilla servers
        auto-clamp to Lv 65 / IV 100 / Souls 20. Winners pick their
        target server when entering (same flow as the Discord bot).
      </p>
    </div>
  );
}
